import { AbyssTheme as themeConfiguration } from '@src/app/browser';
import { chartColors } from '@src/shared/ui/components/Chart/includes/chartColors';
import { ErrorHandler } from '@src/shared/ui/components/ErrorHandler';
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LogarithmicScale,
  Title,
  Tooltip,
} from 'chart.js';
import { isEmpty, isUndefined, shuffle } from 'lodash';
import pattern from 'patternomaly';
import React, { useMemo } from 'react';
import { Bar } from 'react-chartjs-2';

import { getTooltipFooter, getTooltipLabel, getTooltipTitle } from '../../includes/functions';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, LogarithmicScale);

/**
 * VerticalBarChart
 *
 * A vertical bar chart is a graphical representation of data using vertical bars to display the relative sizes or
 * frequencies of different categories or values along a horizontal axis.
 *
 * @param data
 * @param dataSets
 * @param labels
 * @param legendLimit
 * @param showLegend
 * @param tooltips
 * @returns {Element}
 * @constructor
 */
export function VerticalBarChart({
  data = [],
  dataSets = [],
  labels = [],
  legendLimit = 10,
  showLegend = true,
  tooltips = [],
}) {
  /**
   * Defines an array of colors and patterns to use for rendering the datasets.
   *
   * @type {Array<CanvasPattern>}
   */
  const patterns = useMemo(() => {
    return pattern.generate(
      shuffle(chartColors).map((colorName) => {
        return themeConfiguration?.theme?.colors?.[`${colorName}`];
      })
    );
  }, [themeConfiguration]);

  const theDataSets = useMemo(() => {
    let items = [];
    if (!isEmpty(data)) {
      data?.forEach((item) => {
        items.push(Number(item?.value));
      });

      return [
        {
          backgroundColor: patterns,
          borderWidth: 1,
          data: items,
          grouped: false,
          label: '',
        },
      ];
    }
    if (!isEmpty(dataSets)) {
      items = dataSets;
    }

    return items;
  }, [data, dataSets]);

  const theLabels = useMemo(() => {
    let items = [];
    if (!isEmpty(data)) {
      data?.forEach((item) => {
        items.push(String(item?.name));
      });
    } else if (!isEmpty(labels)) {
      items = labels;
    }
    return items;
  }, [data, labels]);

  const options = useMemo(() => {
    const theOptions = {
      animation: {
        duration: 0, // general animation time
      },
      hover: {
        animationDuration: 0, // duration of animations when hovering an item
      },
      plugins: {
        legend: {
          display: isUndefined(showLegend) ? !isUndefined(legendLimit) && labels.length <= legendLimit : true,
        },
      },
      responsive: true,
      responsiveAnimationDuration: 0, // animation duration after a resize
    };

    if (!isEmpty(data)) {
      theOptions.plugins.tooltip = {
        callbacks: {
          footer(args) {
            return getTooltipFooter(args, data);
          },
          label(args) {
            return getTooltipLabel(args, data);
          },
          title(args) {
            return getTooltipTitle(args, data);
          },
        },
      };
    }

    if (!isEmpty(tooltips)) {
      theOptions.plugins.tooltip = {
        callbacks: {
          footer(args) {
            return getTooltipFooter(args, data, tooltips);
          },
          label(args) {
            return getTooltipLabel(args, data, tooltips);
          },
          title(args) {
            return getTooltipTitle(args, data, tooltips);
          },
        },
      };
    }

    return theOptions;
  }, [showLegend, legendLimit, labels, data, tooltips]);

  return (
    <ErrorHandler location="src/shared/ui/components/Chart/components/VerticalBarChart/VerticalBarChart.jsx">
      <Bar
        data={{
          datasets: theDataSets,
          labels: theLabels,
        }}
        options={options}
      />
    </ErrorHandler>
  );
}
